import { FEATURES, logger, ConsoleLogSink, buildPageEvent, buildTrackEvent, buildIdentifyEvent, unionBy, NinetailedApiClient, OnLogLogSink, OnErrorLogSink, PageviewProperties, Properties, Traits, pipe } from '@ninetailed/experience.js-shared';
export { EXPERIENCE_TRAIT_PREFIX, isExperienceMatch, selectActiveExperiments, selectDistribution, selectExperience, selectBaselineWithVariants as selectExperienceBaselineWithVariants, selectVariant as selectExperienceVariant, selectVariants as selectExperienceVariants, selectHasVariants as selectHasExperienceVariants } from '@ninetailed/experience.js-shared';
import Analytics from 'analytics';
import { z } from 'zod';

const HAS_SEEN_COMPONENT = 'has_seen_component';
const HAS_SEEN_ELEMENT = 'has_seen_element';
const COMPONENT = 'component';
const COMPONENT_START = 'componentStart';
const PAGE_HIDDEN = 'page_hidden';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const buildClientLocale = () => navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;

const buildClientNinetailedRequestContext = () => ({
  url: window.location.href,
  referrer: document.referrer,
  locale: buildClientLocale(),
  userAgent: navigator.userAgent,
  document: {
    title: document.title
  }
});

/**
 * Similar to _.throttle but waits for the promise to resolve.
 * There is no "wait time" because you can simply await `Promise.timeout` inside `fn` to wait some time before the next call.
 */
function asyncThrottle(fn) {
  let runningPromise;
  let queuedPromise;
  let nextArgs;
  return args => __awaiter(this, void 0, void 0, function* () {
    if (runningPromise) {
      nextArgs = args;
      if (queuedPromise) {
        return queuedPromise;
      } else {
        queuedPromise = runningPromise.then(() => {
          queuedPromise = undefined;
          runningPromise = fn(nextArgs);
          return runningPromise;
        });
        return queuedPromise;
      }
    } else {
      runningPromise = fn(args);
      return runningPromise;
    }
  });
}

const LEGACY_ANONYMOUS_ID = '__anon_id';
const ANONYMOUS_ID = '__nt_anonymous_id__';
const DEBUG_FLAG = '__nt_debug__';
const PROFILE_FALLBACK_CACHE = '__nt_profile__';
const EXPERIENCES_FALLBACK_CACHE = '__nt_experiences__';
const PROFILE_CHANGE = 'profile-change';
const PROFILE_RESET = 'profile-reset';
const CONSENT = '__nt-consent__';
const SET_ENABLED_FEATURES = 'set-enabled-features';
const EMPTY_MERGE_ID = 'nt:empty-merge-id';

const PLUGIN_NAME = 'ninetailed';
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
const ninetailedCorePlugin = ({
  apiClient,
  locale,
  ninetailed,
  onInitProfileId,
  buildClientContext
}) => {
  let _instance;
  let queue = [];
  let enabledFeatures = Object.values(FEATURES);
  const buildContext = buildClientContext || buildClientNinetailedRequestContext;
  const flush = () => __awaiter(void 0, void 0, void 0, function* () {
    const events = Object.assign([], queue);
    logger.info('Start flushing events.');
    queue = [];
    if (!events.length) {
      return {
        success: true
      };
    }
    try {
      const anonymousId = _instance.storage.getItem(ANONYMOUS_ID);
      const {
        profile,
        experiences
      } = yield apiClient.upsertProfile({
        profileId: anonymousId,
        events
      }, {
        locale,
        enabledFeatures
      });
      _instance.storage.setItem(ANONYMOUS_ID, profile.id);
      _instance.storage.setItem(PROFILE_FALLBACK_CACHE, profile);
      _instance.storage.setItem(EXPERIENCES_FALLBACK_CACHE, experiences);
      logger.debug('Profile from api: ', profile);
      logger.debug('Experiences from api: ', experiences);
      _instance.dispatch({
        type: PROFILE_CHANGE,
        profile,
        experiences
      });
      yield delay(20);
      return {
        success: true
      };
    } catch (error) {
      logger.debug('An error occurred during flushing the events: ', error);
      const fallbackProfile = _instance.storage.getItem(PROFILE_FALLBACK_CACHE);
      const fallbackExperiences = _instance.storage.getItem(EXPERIENCES_FALLBACK_CACHE) || [];
      if (fallbackProfile) {
        logger.debug('Found a fallback profile - will use this.');
        _instance.dispatch({
          type: PROFILE_CHANGE,
          profile: fallbackProfile,
          experiences: fallbackExperiences
        });
      } else {
        logger.debug('No fallback profile found - setting profile to null.');
        _instance.dispatch({
          type: PROFILE_CHANGE,
          profile: null,
          experiences: fallbackExperiences,
          error
        });
      }
      return {
        success: false
      };
    }
  });
  const enqueueEvent = event => __awaiter(void 0, void 0, void 0, function* () {
    queue = unionBy([event], queue, 'messageId');
  });
  const abortNonClientEvents = ({
    abort,
    payload
  }) => {
    if (typeof window !== 'object') {
      return abort();
    }
    return payload;
  };
  return {
    name: 'ninetailed',
    config: {},
    initialize: ({
      instance
    }) => __awaiter(void 0, void 0, void 0, function* () {
      _instance = instance;
      if (instance.storage.getItem(DEBUG_FLAG)) {
        logger.addSink(new ConsoleLogSink());
        logger.info('Ninetailed Debug Mode is enabled.');
      }
      // legacy support for the old anonymousId
      const legacyAnonymousId = instance.storage.getItem(LEGACY_ANONYMOUS_ID);
      if (legacyAnonymousId) {
        logger.debug('Found legacy anonymousId, migrating to new one.', legacyAnonymousId);
        instance.storage.setItem(ANONYMOUS_ID, legacyAnonymousId);
        instance.storage.removeItem(LEGACY_ANONYMOUS_ID);
      }
      if (typeof onInitProfileId === 'function') {
        const profileId = yield onInitProfileId(instance.storage.getItem(ANONYMOUS_ID));
        if (typeof profileId === 'string') {
          instance.storage.setItem(ANONYMOUS_ID, profileId);
        }
      }
      instance.on(SET_ENABLED_FEATURES, ({
        payload
      }) => {
        enabledFeatures = payload.features || [];
      });
      logger.debug('Ninetailed Core plugin initialized.');
    }),
    flush: asyncThrottle(flush),
    pageStart: params => {
      return abortNonClientEvents(params);
    },
    page: ({
      payload
    }) => __awaiter(void 0, void 0, void 0, function* () {
      logger.info('Sending Page event.');
      const ctx = buildContext();
      return enqueueEvent(buildPageEvent({
        messageId: payload.meta.rid,
        timestamp: payload.meta.ts,
        properties: payload.properties,
        ctx
      }));
    }),
    trackStart: params => {
      return abortNonClientEvents(params);
    },
    track: ({
      payload
    }) => __awaiter(void 0, void 0, void 0, function* () {
      logger.info('Sending Track event.');
      const ctx = buildContext();
      return enqueueEvent(buildTrackEvent({
        messageId: payload.meta.rid,
        timestamp: payload.meta.ts,
        event: payload.event,
        properties: payload.properties,
        ctx
      }));
    }),
    identifyStart: params => {
      return abortNonClientEvents(params);
    },
    identify: ({
      payload
    }) => __awaiter(void 0, void 0, void 0, function* () {
      logger.info('Sending Identify event.');
      const ctx = buildContext();
      if (payload.userId === EMPTY_MERGE_ID && (!payload.traits || typeof payload.traits === 'object' && Object.keys(payload.traits).length === 0)) {
        logger.info('Skipping Identify event as no userId and no traits are set.');
        return;
      }
      return enqueueEvent(buildIdentifyEvent({
        messageId: payload.meta.rid,
        timestamp: payload.meta.ts,
        traits: payload.traits,
        userId: payload.userId === EMPTY_MERGE_ID ? '' : payload.userId,
        ctx
      }));
    }),
    setItemStart: ({
      abort,
      payload
    }) => {
      if (![ANONYMOUS_ID, DEBUG_FLAG, PROFILE_FALLBACK_CACHE, EXPERIENCES_FALLBACK_CACHE, CONSENT].includes(payload.key)) {
        return abort();
      }
      return payload;
    },
    methods: {
      reset: (...args) => __awaiter(void 0, void 0, void 0, function* () {
        logger.debug('Resetting profile.');
        const instance = args[args.length - 1];
        instance.dispatch({
          type: PROFILE_RESET
        });
        instance.storage.removeItem(ANONYMOUS_ID);
        instance.storage.removeItem(PROFILE_FALLBACK_CACHE);
        instance.storage.removeItem(EXPERIENCES_FALLBACK_CACHE);
        logger.debug('Removed old profile data from localstorage.');
        if (typeof onInitProfileId === 'function') {
          const profileId = yield onInitProfileId(undefined);
          if (typeof profileId === 'string') {
            instance.storage.setItem(ANONYMOUS_ID, profileId);
          }
        }
        yield ninetailed.track('nt_reset');
        logger.info('Profile reset successful.');
        yield delay(10);
      }),
      debug: (...args) => __awaiter(void 0, void 0, void 0, function* () {
        const enabled = args[0];
        const instance = args[args.length - 1];
        const consoleLogSink = new ConsoleLogSink();
        if (enabled) {
          instance.storage.setItem(DEBUG_FLAG, true);
          logger.addSink(consoleLogSink);
          logger.info('Debug mode enabled.');
        } else {
          instance.storage.removeItem(DEBUG_FLAG);
          logger.info('Debug mode disabled.');
          logger.removeSink(consoleLogSink.name);
        }
      })
    }
  };
};

class ElementSeenObserver {
  constructor(_options) {
    this._options = _options;
    this._elementDelays = new WeakMap();
    this._intersectionTimers = new WeakMap();
    if (typeof IntersectionObserver !== 'undefined') {
      this._intersectionObserver = new IntersectionObserver(this.onIntersection.bind(this));
    }
  }
  onIntersection(entries) {
    entries.forEach(entry => {
      const {
        isIntersecting,
        target
      } = entry;
      if (isIntersecting) {
        const delay = this._elementDelays.get(target);
        const timeOut = window.setTimeout(() => {
          this._options.onElementSeen(target);
        }, delay);
        this._intersectionTimers.set(target, timeOut);
      } else {
        const timeOut = this._intersectionTimers.get(target);
        if (typeof timeOut !== 'undefined') {
          window.clearTimeout(timeOut);
        }
      }
    });
  }
  observe(element, options) {
    var _a, _b;
    this._elementDelays.set(element, (_a = options === null || options === void 0 ? void 0 : options.delay) !== null && _a !== void 0 ? _a : 2000);
    (_b = this._intersectionObserver) === null || _b === void 0 ? void 0 : _b.observe(element);
  }
  unobserve(element) {
    var _a;
    (_a = this._intersectionObserver) === null || _a === void 0 ? void 0 : _a.unobserve(element);
  }
}

const acceptsCredentials = plugin => {
  return typeof plugin === 'object' && plugin !== null && 'setCredentials' in plugin && typeof plugin.setCredentials === 'function';
};

const isInterestedInHiddenPage = arg => {
  return typeof arg === 'object' && arg !== null && PAGE_HIDDEN in arg && typeof arg[PAGE_HIDDEN] === 'function';
};

class Ninetailed {
  constructor(ninetailedApiClientInstanceOrOptions, {
    plugins,
    url,
    locale,
    requestTimeout,
    onLog,
    onError,
    buildClientContext,
    onInitProfileId,
    componentViewTrackingThreshold = 2000,
    storageImpl
  } = {}) {
    this.isInitialized = false;
    this.page = (data, options) => __awaiter(this, void 0, void 0, function* () {
      try {
        const result = PageviewProperties.partial().default({}).safeParse(data);
        if (!result.success) {
          throw new Error(`[Validation Error] "page" was called with invalid params. Page data is not valid: ${result.error.format()}`);
        }
        yield this.waitUntilInitialized();
        yield this.instance.page(data, this.buildOptions(options));
        return this.ninetailedCorePlugin.flush();
      } catch (error) {
        logger.error(error);
        if (error instanceof RangeError) {
          throw new Error(`[Validation Error] "page" was called with invalid params. Could not validate due to "RangeError: Maximum call stack size exceeded". This can be caused by passing a cyclic data structure as a parameter. Refrain from passing a cyclic data structure or sanitize it beforehand.`);
        }
        throw error;
      }
    });
    this.track = (event, properties, options) => __awaiter(this, void 0, void 0, function* () {
      try {
        const result = Properties.default({}).safeParse(properties);
        if (!result.success) {
          throw new Error(`[Validation Error] "track" was called with invalid params. Properties are no valid json object: ${result.error.format()}`);
        }
        yield this.waitUntilInitialized();
        yield this.instance.track(event.toString(), result.data, this.buildOptions(options));
        return this.ninetailedCorePlugin.flush();
      } catch (error) {
        logger.error(error);
        if (error instanceof RangeError) {
          throw new Error(`[Validation Error] "track" was called with invalid params. Could not validate due to "RangeError: Maximum call stack size exceeded". This can be caused by passing a cyclic data structure as a parameter. Refrain from passing a cyclic data structure or sanitize it beforehand.`);
        }
        throw error;
      }
    });
    /**
     * @deprecated The legacy datamodel is not recommended anymore
     * Will be removed in the next version of the SDK
     */
    this.trackHasSeenComponent = properties => __awaiter(this, void 0, void 0, function* () {
      return this.instance.dispatch(Object.assign(Object.assign({}, properties), {
        type: HAS_SEEN_COMPONENT
      }));
    });
    this.trackComponentView = properties => {
      return this.instance.dispatch(Object.assign(Object.assign({}, properties), {
        type: HAS_SEEN_ELEMENT
      }));
    };
    this.observeElement = (payload, options) => {
      const {
          element
        } = payload,
        remaingPayload = __rest(payload, ["element"]);
      if (!(element instanceof Element)) {
        const isObject = typeof element === 'object' && element !== null;
        const constructorName = isObject ? element.constructor.name : '';
        const isConstructorNameNotObject = constructorName && constructorName !== 'Object';
        logger.warn(`ElementSeenObserver.observeElement was called with an invalid element. Expected an Element but got ${typeof element}${isConstructorNameNotObject ? ` of type ${constructorName}` : ''}. This call will be ignored.`);
      } else {
        this.observedElements.set(element, remaingPayload);
        this.elementSeenObserver.observe(element, Object.assign({
          delay: this.componentViewTrackingThreshold
        }, options));
      }
    };
    this.unobserveElement = element => {
      this.observedElements.delete(element);
      this.elementSeenObserver.unobserve(element);
    };
    this.onElementSeen = element => {
      const payload = this.observedElements.get(element);
      if (typeof payload !== 'undefined') {
        this.trackComponentView(Object.assign({
          element
        }, payload));
      }
    };
    this.identify = (uid, traits, options) => __awaiter(this, void 0, void 0, function* () {
      try {
        const result = Traits.default({}).safeParse(traits);
        if (!result.success) {
          throw new Error(`[Validation Error] "identify" was called with invalid params. Traits are no valid json: ${result.error.format()}`);
        }
        yield this.waitUntilInitialized();
        yield this.instance.identify(uid && uid.toString() !== '' ? uid.toString() : EMPTY_MERGE_ID, result.data, this.buildOptions(options));
        return this.ninetailedCorePlugin.flush();
      } catch (error) {
        logger.error(error);
        if (error instanceof RangeError) {
          throw new Error(`[Validation Error] "identify" was called with invalid params. Could not validate due to "RangeError: Maximum call stack size exceeded". This can be caused by passing a cyclic data structure as a parameter. Refrain from passing a cyclic data structure or sanitize it beforehand.`);
        }
        throw error;
      }
    });
    this.reset = () => __awaiter(this, void 0, void 0, function* () {
      yield this.waitUntilInitialized();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.instance.plugins[PLUGIN_NAME].reset();
    });
    this.debug = enabled => __awaiter(this, void 0, void 0, function* () {
      yield this.waitUntilInitialized();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.instance.plugins[PLUGIN_NAME].debug(enabled);
    });
    this.onProfileChange = cb => {
      cb(this.profileState);
      return this.instance.on(PROFILE_CHANGE, ({
        payload
      }) => {
        if (payload.error) {
          cb(Object.assign(Object.assign({}, this._profileState), {
            status: 'error',
            profile: payload.profile,
            experiences: payload.experiences,
            error: payload.error
          }));
        } else {
          cb(Object.assign(Object.assign({}, this._profileState), {
            status: 'success',
            profile: payload.profile,
            experiences: payload.experiences,
            error: null
          }));
        }
      });
    };
    this.onIsInitialized = onIsInitialized => {
      if (typeof onIsInitialized === 'function') {
        if (this.isInitialized) {
          onIsInitialized();
        } else {
          const detachOnReadyListener = this.instance.on('ready', () => {
            this.isInitialized = true;
            onIsInitialized();
            detachOnReadyListener();
          });
        }
      }
    };
    this.waitUntilInitialized = () => {
      return new Promise(resolve => {
        this.onIsInitialized(resolve);
      });
    };
    this.onVisibilityChange = () => {
      if (typeof document === 'undefined') {
        return;
      }
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.instance.dispatch({
            type: PAGE_HIDDEN
          });
        }
      });
    };
    if (ninetailedApiClientInstanceOrOptions instanceof NinetailedApiClient) {
      this.apiClient = ninetailedApiClientInstanceOrOptions;
    } else {
      const {
        clientId,
        environment,
        preview
      } = ninetailedApiClientInstanceOrOptions;
      this.clientId = clientId;
      this.environment = environment || 'main';
      this.apiClient = new NinetailedApiClient({
        clientId,
        environment,
        url,
        preview
      });
    }
    this.plugins = (plugins !== null && plugins !== void 0 ? plugins : []).flat();
    this.plugins.forEach(plugin => {
      if (acceptsCredentials(plugin) && this.clientId && this.environment) {
        plugin.setCredentials({
          clientId: this.clientId,
          environment: this.environment
        });
      }
    });
    this._profileState = {
      status: 'loading',
      profile: null,
      experiences: null,
      error: null,
      from: 'api'
    };
    if (typeof onLog === 'function') {
      logger.addSink(new OnLogLogSink(onLog));
    }
    if (typeof onError === 'function') {
      logger.addSink(new OnErrorLogSink(onError));
    }
    this.logger = logger;
    this.ninetailedCorePlugin = ninetailedCorePlugin({
      apiClient: this.apiClient,
      locale,
      requestTimeout,
      buildClientContext,
      onInitProfileId,
      ninetailed: this
    });
    this.instance = Analytics(Object.assign({
      app: 'ninetailed',
      plugins: [...this.plugins, this.ninetailedCorePlugin]
    }, storageImpl ? {
      storage: storageImpl
    } : {}));
    const detachOnReadyListener = this.instance.on('ready', () => {
      this.isInitialized = true;
      logger.info('Ninetailed Experience.js SDK is completely initialized.');
      detachOnReadyListener();
    });
    // put in private method
    this.onProfileChange(profileState => {
      this._profileState = profileState;
      if (typeof window !== 'undefined') {
        window.ninetailed = Object.assign({}, window.ninetailed, {
          profile: this.profileState.profile,
          experiences: this.profileState.experiences
        });
      }
    });
    this.observedElements = new WeakMap();
    this.elementSeenObserver = new ElementSeenObserver({
      onElementSeen: this.onElementSeen.bind(this)
    });
    this.componentViewTrackingThreshold = componentViewTrackingThreshold;
    const hasPluginsInterestedInHiddenPage = this.plugins.some(isInterestedInHiddenPage);
    if (hasPluginsInterestedInHiddenPage) {
      this.onVisibilityChange();
    }
    this.registerWindowHandlers();
  }
  get profileState() {
    return this._profileState;
  }
  buildOptions(options = {}) {
    return Object.assign({}, options);
  }
  registerWindowHandlers() {
    if (typeof window !== 'undefined') {
      window.ninetailed = Object.assign({}, window.ninetailed, {
        page: this.page.bind(this),
        track: this.track.bind(this),
        identify: this.identify.bind(this),
        reset: this.reset.bind(this),
        debug: this.debug.bind(this),
        profile: this.profileState.profile
      });
    }
  }
}

const selectVariant = (baseline, variants, {
  status,
  profile,
  error
}, options = {
  holdout: -1
}) => {
  if (status === 'loading') {
    return {
      loading: true,
      variant: Object.assign(Object.assign({}, baseline), {
        id: 'baseline',
        audience: {
          id: 'baseline'
        }
      }),
      audience: {
        id: 'baseline'
      },
      isPersonalized: false,
      error: null
    };
  }
  if (status === 'error') {
    return {
      loading: false,
      variant: Object.assign(Object.assign({}, baseline), {
        id: 'baseline',
        audience: {
          id: 'baseline'
        }
      }),
      audience: {
        id: 'baseline'
      },
      isPersonalized: false,
      error: error
    };
  }
  const variant = variants.find(variant => {
    var _a, _b;
    return (_a = profile === null || profile === void 0 ? void 0 : profile.audiences) === null || _a === void 0 ? void 0 : _a.includes((_b = variant.audience) === null || _b === void 0 ? void 0 : _b.id);
  });
  if (variant) {
    if ((options === null || options === void 0 ? void 0 : options.holdout) || -1 > ((profile === null || profile === void 0 ? void 0 : profile.random) || 0)) {
      return {
        loading: false,
        variant: Object.assign(Object.assign({}, baseline), {
          audience: {
            id: 'baseline'
          }
        }),
        audience: Object.assign(Object.assign({}, variant.audience), {
          id: variant.audience.id
        }),
        isPersonalized: false,
        error: null
      };
    }
    return {
      loading: false,
      variant,
      audience: Object.assign(Object.assign({}, variant.audience), {
        id: variant.audience.id
      }),
      isPersonalized: true,
      error: null
    };
  }
  /**
   * There was no matching audience found.
   */
  return {
    loading: false,
    variant: Object.assign(Object.assign({}, baseline), {
      id: 'baseline',
      audience: {
        id: 'baseline'
      }
    }),
    audience: {
      id: 'baseline'
    },
    isPersonalized: false,
    error: null
  };
};

const TrackComponentProperties = z.object({
  variant: z.object({
    id: z.string()
  }),
  audience: z.object({
    id: z.string()
  }),
  isPersonalized: z.boolean()
});

class NinetailedPlugin {}

const ElementSeenPayloadSchema = z.object({
  element: z.any(),
  experience: z.object({
    id: z.string(),
    type: z.union([z.literal('nt_experiment'), z.literal('nt_personalization')]),
    name: z.string().optional(),
    description: z.string().optional()
  }).optional().nullable(),
  audience: z.object({
    id: z.string(),
    name: z.string().optional(),
    description: z.string().optional()
  }).optional().nullable().default({
    id: 'ALL_VISITORS',
    name: 'All Visitors',
    description: 'This is the default all visitors audience as no audience was set.'
  }),
  variant: z.object({
    id: z.string()
  }).catchall(z.unknown()),
  variantIndex: z.number()
});

const decodeExperienceVariantsMap = encodedExperienceVariantsMap => {
  return encodedExperienceVariantsMap.split(',').map(experienceIdWithVariant => {
    const [experienceId, _variantIndex] = experienceIdWithVariant.split('=');
    const variantIndex = parseInt(_variantIndex);
    if (!experienceId || !variantIndex) {
      return null;
    }
    return {
      experienceId,
      variantIndex
    };
  }).filter(x => !!x).reduce((acc, curr) => Object.assign(Object.assign({}, acc), {
    [curr.experienceId]: curr.variantIndex
  }), {});
};

class OnChangeEmitter {
  constructor() {
    this.onChangeListeners = [];
  }
  addListener(listener) {
    this.onChangeListeners.push(listener);
    return () => {
      this.removeOnChangeListener(listener);
    };
  }
  invokeListeners() {
    this.onChangeListeners.forEach(listener => listener());
  }
  removeOnChangeListener(listener) {
    this.onChangeListeners = this.onChangeListeners.filter(l => l !== listener);
  }
}

const hasOnChangeEmitter = arg => {
  return typeof arg === 'object' && arg !== null && 'onChangeEmitter' in arg && typeof arg.onChangeEmitter === 'object' && arg.onChangeEmitter !== null && arg.onChangeEmitter.constructor === OnChangeEmitter;
};

const selectPluginsHavingOnChangeEmitter = plugins => {
  const filteredPlugins = [];
  for (const plugin of plugins) {
    if (hasOnChangeEmitter(plugin)) {
      filteredPlugins.push(plugin);
    }
  }
  return filteredPlugins;
};

const hasExperienceSelectionMiddleware = arg => {
  return typeof arg === 'object' && arg !== null && 'getExperienceSelectionMiddleware' in arg && typeof arg.getExperienceSelectionMiddleware === 'function';
};

const selectPluginsHavingExperienceSelectionMiddleware = plugins => {
  const filteredPlugins = [];
  for (const plugin of plugins) {
    if (hasExperienceSelectionMiddleware(plugin)) {
      filteredPlugins.push(plugin);
    }
  }
  return filteredPlugins;
};

const createPassThroughMiddleware = () => {
  return ({
    experience,
    variant,
    variantIndex
  }) => {
    return {
      experience,
      variant,
      variantIndex
    };
  };
};
const makeExperienceSelectMiddleware = ({
  plugins,
  onChange,
  experiences,
  baseline,
  profile
}) => {
  let removeChangeListeners = [];
  const pluginsHavingChangeEmitters = selectPluginsHavingOnChangeEmitter(plugins);
  const prepareMiddleware = () => {
    if (profile === null) {
      return createPassThroughMiddleware();
    }
    const pluginsWithMiddleware = selectPluginsHavingExperienceSelectionMiddleware(plugins);
    const middlewareFunctions = pluginsWithMiddleware.map(plugin => plugin.getExperienceSelectionMiddleware({
      experiences,
      baseline
    }));
    return pipe(...middlewareFunctions);
  };
  const addListeners = () => {
    removeChangeListeners = pluginsHavingChangeEmitters.map(plugin => {
      const listener = () => {
        onChange();
      };
      return plugin.onChangeEmitter.addListener(listener);
    });
  };
  const removeListeners = () => {
    removeChangeListeners.forEach(listener => listener());
  };
  const middleware = prepareMiddleware();
  return {
    addListeners,
    removeListeners,
    middleware
  };
};

export { ANONYMOUS_ID, COMPONENT, COMPONENT_START, CONSENT, DEBUG_FLAG, EMPTY_MERGE_ID, EXPERIENCES_FALLBACK_CACHE, ElementSeenPayloadSchema, HAS_SEEN_COMPONENT, HAS_SEEN_ELEMENT, LEGACY_ANONYMOUS_ID, Ninetailed, NinetailedPlugin, OnChangeEmitter, PAGE_HIDDEN, PLUGIN_NAME, PROFILE_CHANGE, PROFILE_FALLBACK_CACHE, PROFILE_RESET, SET_ENABLED_FEATURES, TrackComponentProperties, buildClientNinetailedRequestContext, decodeExperienceVariantsMap, makeExperienceSelectMiddleware, ninetailedCorePlugin, selectPluginsHavingExperienceSelectionMiddleware, selectPluginsHavingOnChangeEmitter, selectVariant };
